/**
 * Standardized Button Components
 *
 * Consistent button styles used throughout the application
 */

/**
 * IconButton - Standard icon-only button
 * Used for: Home, Collapse, Settings, Header nav buttons, Logout, etc.
 *
 * Uses CSS variables for theme-aware hover colors.
 *
 * @param {Object} props
 * @param {Function} props.onClick - Click handler
 * @param {string} props.title - Tooltip text
 * @param {React.ReactNode} props.children - Icon SVG element
 * @param {string} props.className - Additional classes (optional)
 * @param {Object} props.style - Additional inline styles (optional)
 */
export function IconButton({ onClick, title, children, className = '', style = {}, ...props }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 flex items-center justify-center ${className}`}
      style={{
        color: 'var(--color-text-muted)',
        backgroundColor: 'transparent',
        ...style
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--color-text-primary)';
        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--color-text-muted)';
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      title={title}
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * NavButton - Navigation button with icon and text
 * Used for: Library, Forum sidebar buttons
 *
 * Uses CSS variables for theme-aware hover colors.
 *
 * @param {Object} props
 * @param {Function} props.onClick - Click handler
 * @param {string} props.title - Tooltip text
 * @param {React.ReactNode} props.icon - Icon SVG element
 * @param {string} props.label - Button text label
 * @param {string} props.className - Additional classes (optional)
 */
export function NavButton({ onClick, title, icon, label, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium bg-gray-800/50 border border-gray-700 transition-all duration-200 ${className}`}
      style={{
        color: 'var(--color-text-muted)',
        backgroundColor: 'rgba(31, 41, 55, 0.5)',
        borderColor: 'rgb(55, 65, 81)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = 'var(--color-text-primary)';
        e.currentTarget.style.backgroundColor = 'rgb(31, 41, 55)';
        e.currentTarget.style.borderColor = 'rgb(75, 85, 99)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = 'var(--color-text-muted)';
        e.currentTarget.style.backgroundColor = 'rgba(31, 41, 55, 0.5)';
        e.currentTarget.style.borderColor = 'rgb(55, 65, 81)';
      }}
      title={title}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

/**
 * HeaderIconButton - Icon button for header (same as IconButton but without /50 opacity on hover bg)
 * Used for: Header navigation buttons (Library, Forum, Admin in header)
 *
 * @param {Object} props
 * @param {Function} props.onClick - Click handler
 * @param {string} props.title - Tooltip text
 * @param {React.ReactNode} props.children - Icon SVG element
 * @param {string} props.className - Additional classes (optional)
 */
export function HeaderIconButton({ onClick, title, children, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-200 ${className}`}
      title={title}
    >
      {children}
    </button>
  );
}
